import { Tables } from '@/integrations/supabase/types';

const SITE_URL = 'https://carstreet.ca';

const getBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return SITE_URL;
};

export const generateVehicleStructuredData = (
  vehicle: Tables<'vehicles'>,
  primaryImageUrl?: string,
  allImageUrls?: string[]
) => {
  const baseUrl = getBaseUrl();

  // Build image array: use all images if available, fallback to primary, never use placeholder
  const imageList = allImageUrls && allImageUrls.length > 0
    ? allImageUrls
    : primaryImageUrl
      ? [primaryImageUrl]
      : undefined;

  // Always provide a description — Google requires this field
  const description = vehicle.description
    || `${vehicle.year} ${vehicle.make} ${vehicle.model} with ${vehicle.mileage.toLocaleString()} km. ${vehicle.transmission} transmission, ${vehicle.fuel_type} fuel type, ${vehicle.drivetrain} drivetrain. ${vehicle.color} exterior${vehicle.interior_color ? `, ${vehicle.interior_color} interior` : ''}. Available at Car Street, Langton ON.`;

  const structuredData: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': ['Vehicle', 'Product'],
    name: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
    description,
    vehicleIdentificationNumber: vehicle.vin,
    brand: {
      '@type': 'Brand',
      name: vehicle.make,
    },
    manufacturer: {
      '@type': 'Organization',
      name: vehicle.make,
    },
    model: vehicle.model,
    productionDate: vehicle.year.toString(),
    modelDate: vehicle.year.toString(),
    mileageFromOdometer: {
      '@type': 'QuantitativeValue',
      value: vehicle.mileage,
      unitCode: 'KMT',
    },
    vehicleTransmission: vehicle.transmission,
    fuelType: vehicle.fuel_type,
    driveWheelConfiguration: vehicle.drivetrain,
    vehicleEngine: {
      '@type': 'EngineSpecification',
      name: vehicle.engine || 'Not specified',
    },
    color: vehicle.color,
    itemCondition: getConditionUrl(vehicle.condition),
    offers: {
      '@type': 'Offer',
      price: vehicle.price,
      priceCurrency: 'CAD',
      availability: getAvailabilityUrl(vehicle.status),
      url: `${baseUrl}/vehicle/${vehicle.id}`,
      priceValidUntil: getPriceValidUntil(),
      seller: {
        '@type': 'AutoDealer',
        name: 'Car Street',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '17 Queen St',
          addressLocality: 'Langton',
          addressRegion: 'ON',
          postalCode: 'N0E 1G0',
          addressCountry: 'CA',
        },
        telephone: '+16398990000',
      },
      // Return policy — resolves "Missing field hasMerchantReturnPolicy"
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'CA',
        returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
        merchantReturnDays: 0,
      },
      // Shipping details — resolves "Missing field shippingDetails"
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'CA',
          addressRegion: ['ON'],
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 0,
            maxValue: 1,
            unitCode: 'DAY',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 0,
            maxValue: 0,
            unitCode: 'DAY',
          },
        },
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: 0,
          currency: 'CAD',
        },
        shippingLabel: 'Pickup at dealership',
      },
    },
    url: `${baseUrl}/vehicle/${vehicle.id}`,
    sku: vehicle.vin,
  };

  // Only include image if we have a real image URL — resolves "Missing field image" error
  if (imageList) {
    structuredData.image = imageList.length === 1 ? imageList[0] : imageList;
  }

  return structuredData;
};

/** Returns a date ~1 year from now in YYYY-MM-DD format for priceValidUntil */
const getPriceValidUntil = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().split('T')[0];
};

const getConditionUrl = (condition: string) => {
  const conditionMap: Record<string, string> = {
    excellent: 'https://schema.org/NewCondition',
    good: 'https://schema.org/UsedCondition',
    fair: 'https://schema.org/UsedCondition',
    needs_work: 'https://schema.org/DamagedCondition',
  };
  return conditionMap[condition] || 'https://schema.org/UsedCondition';
};

const getAvailabilityUrl = (status: string) => {
  const availabilityMap: Record<string, string> = {
    available: 'https://schema.org/InStock',
    pending: 'https://schema.org/PreOrder',
    sold: 'https://schema.org/OutOfStock',
  };
  return availabilityMap[status] || 'https://schema.org/InStock';
};

export const generateBreadcrumbSchema = (
  vehicleName: string,
  vehicleId: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://carstreet.ca',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Inventory',
      item: 'https://carstreet.ca/inventory',
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: vehicleName,
      item: `https://carstreet.ca/vehicle/${vehicleId}`,
    },
  ],
});
